import { call, put, select } from 'redux-saga/effects'
import { utils, writeFileXLSX } from 'xlsx'
import dayjs from 'dayjs'
import { fetchChequeDetails, updateChequeDetailsApi } from 'services/Api/ChequeApi'
import { IAppState } from 'infra/AppState'
import { API_RESPONSE_TYPE, IApiResponse } from 'shared/constants'
import { addAutoFadeNotification } from 'pages/Notification/Notification.actions'
import { NotificationType } from 'pages/Notification/Notification.types'
import {
  ChequeState,
  IFetchReceivedActionPayload,
  IFetchChequeRequestPayload,
  UpdateChequeDetailsType,
  UpdateChequeRequestPayload,
  Cheque,
  IFetchOtherActionPayload,
  IChequeState
} from 'state/new-summary/cheque/cheque.types'
import { getOrderColumnWidths } from 'state/orderSummary/orderSummary.helpers'
import { Action } from 'infra/types'
import { CHEQUE_TYPE } from 'pages/NewDashboard/components/Reconcile/Reconcile.types'
import { VIEW } from 'pages/NewDashboard/components/Reconcile/components/DepositedCheque/constants'
import { COLLECTION_FILTER_TYPE } from 'pages/NewDashboard/components/Summary/components/CollectionContainer/CollectionHeader/constants'
import {
  resetEditChequeDrawer,
  setByChequeDateFilterAction,
  setCheques,
  setReceivedCheques,
  setReceivedSortedBy,
  setDepositedSortedBy,
  setFilterTime,
  setOtherCheques,
  setClearedSortedBy,
  fetchReceivedCheques,
  fetchOtherCheques
} from './cheque.actions'
import {
  CheuqueStateToStoreMapper,
  DEFAULT_PAGE_SIZE,
  formatChequeReceivedDataForExcel,
  formatChequesData
} from './cheque.helpers'

export function* fetchReceivedChequesEffect(action: {
  payload: IFetchReceivedActionPayload
  type: string
}) {
  const { searchString, sortOptions, byChequeDate, page, perPage } = action.payload
  const requestPayload: IFetchChequeRequestPayload = {
    page: page || 1,
    per_page: perPage || DEFAULT_PAGE_SIZE,
    filters: {
      cheque_state: ChequeState.RECEIVED,
      ...(searchString && { search_string: searchString }),
      ...(byChequeDate?.startTime &&
        byChequeDate?.endTime && {
          cheque_date_interval: {
            start_time: byChequeDate.startTime,
            end_time: byChequeDate.endTime
          }
        })
    },
    ...(sortOptions && {
      sort_options: {
        sort_by: sortOptions.sortBy,
        sort_order: sortOptions.sortOrder
      }
    })
  }
  const response: IApiResponse = yield call(fetchChequeDetails, requestPayload)
  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Cheque not Loaded, Please Refresh', {
      cause: 'customError'
    })
  }
  const receivedChequesData = formatChequesData(response.data)
  yield put(setReceivedCheques(receivedChequesData))
  yield put(
    setByChequeDateFilterAction({
      byChequeDateFilterType: 'receivedByChequeDateFilter',
      filterValue: byChequeDate?.startTime && byChequeDate.endTime ? byChequeDate : undefined
    })
  )
  yield put(setReceivedSortedBy({ sortBy: sortOptions?.sortBy ? sortOptions.sortBy : '' }))
}

export function* fetchOtherChequesEffect(action: { payload: IFetchOtherActionPayload }) {
  const { filterTime, chequeState, searchString, sortOptions, byChequeDate, page, perPage } =
    action.payload
  let requestPayload: IFetchChequeRequestPayload = {
    page: page || 1,
    per_page: perPage || DEFAULT_PAGE_SIZE,
    filters: {
      cheque_state: chequeState,
      collected_on_interval: {
        start_time: filterTime?.startTime || 0,
        end_time: filterTime?.endTime || 0
      },
      ...(byChequeDate && {
        cheque_date_interval: {
          start_time: byChequeDate.startTime,
          end_time: byChequeDate.endTime
        }
      }),
      ...(searchString && { search_string: searchString })
    },
    ...(sortOptions && {
      sort_options: {
        sort_by: sortOptions.sortBy,
        sort_order: sortOptions.sortOrder
      }
    })
  }

  const response: IApiResponse = yield call(fetchChequeDetails, requestPayload)

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Cheque not Loaded, Please Refresh', {
      cause: 'customError'
    })
  }

  let autoReconciledCheques: any = []
  if (chequeState === ChequeState.CLEARED) {
    requestPayload = {
      ...requestPayload,
      filters: {
        ...requestPayload.filters,
        cheque_state: ChequeState.AUTO_RECONCILED
      }
    }
    const response: IApiResponse = yield call(fetchChequeDetails, requestPayload)
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      autoReconciledCheques = formatChequesData(response.data)
      const updatedChequesData = autoReconciledCheques.cheques.map((cheque: any) => ({
        ...cheque,
        chequeState: ChequeState.CLEARED,
        isAutoReconciled: true
      }))
      autoReconciledCheques.cheques = updatedChequesData
    }
  }

  const formattedData = formatChequesData(response.data)

  const obj = {
    cheques: [...formattedData.cheques, ...(autoReconciledCheques.cheques || [])],
    pagination: formattedData.pagination,
    chequeType: CheuqueStateToStoreMapper[chequeState]
  }
  yield put(setOtherCheques(obj))

  yield put(
    setFilterTime({
      startTime: filterTime?.startTime || 0,
      endTime: filterTime?.endTime || 0,
      filterType: filterTime?.filterType
    })
  )

  if (chequeState === ChequeState.DEPOSITED) {
    yield put(
      setByChequeDateFilterAction({
        byChequeDateFilterType: 'depositedByChequeDateFilter',
        filterValue: byChequeDate?.startTime && byChequeDate.endTime ? byChequeDate : undefined
      })
    )

    yield put(setDepositedSortedBy({ sortBy: sortOptions?.sortBy ? sortOptions.sortBy : '' }))
  } else {
    yield put(
      setByChequeDateFilterAction({
        byChequeDateFilterType: 'clearedByChequeDateFilter',
        filterValue: byChequeDate?.startTime && byChequeDate.endTime ? byChequeDate : undefined
      })
    )
    yield put(setClearedSortedBy({ sortBy: sortOptions?.sortBy ? sortOptions.sortBy : '' }))
  }
}

export function* updateChequeDetailsEffect(action: {
  payload: {
    data: (UpdateChequeDetailsType & { state: ChequeState })[]
    type: 'receivedCheque' | 'depositedCheque' | 'clearedCheque'
  }
}) {
  const requestPayload: UpdateChequeRequestPayload = {
    cheque_details: action.payload.data.map((element) => ({
      ...element,
      cheque_date: Number(element.cheque_date),
      collected_on: Number(element.collected_on)
    }))
  }

  const response: IApiResponse = yield call(updateChequeDetailsApi, requestPayload)

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Cheque Updation Failed', {
      cause: 'customError'
    })
  }

  const { data, type } = action.payload
  const {
    pagination: {
      [type]: { currentPage, pageSize, totalEntries }
    },
    filterTime
  } = yield select((app: IAppState) => app.Reconcile.Cheque)
  const totalPages = Math.ceil(totalEntries / pageSize)
  const itemsInLastPage = totalEntries % pageSize
  const isLastPage = totalPages === currentPage
  const pageToBeFetched =
    isLastPage && data.length === itemsInLastPage ? Math.max(currentPage - 1, 1) : currentPage
  if (type === 'receivedCheque') {
    yield put(fetchReceivedCheques({ page: pageToBeFetched }))
  } else if (type === 'depositedCheque') {
    yield put(
      fetchOtherCheques({ chequeState: ChequeState.DEPOSITED, filterTime, page: pageToBeFetched })
    )
  } else if (type === 'clearedCheque') {
    yield put(
      fetchOtherCheques({ chequeState: ChequeState.CLEARED, filterTime, page: pageToBeFetched })
    )
  }
}

export function* editChequeDetailsEffect(action: {
  payload: {
    data: UpdateChequeDetailsType & { state: ChequeState }
    type: 'receivedCheque' | 'depositedCheque' | 'clearedCheque'
  }
}) {
  const { data, type } = action.payload
  const requestPayload: UpdateChequeRequestPayload = {
    cheque_details: [data]
  }

  const response: IApiResponse = yield call(updateChequeDetailsApi, requestPayload)

  if (response.type === API_RESPONSE_TYPE.FAILURE) {
    throw new Error('Cheque Updation Failed', {
      cause: 'customError'
    })
  }
  const storeData: Cheque[] = yield select((app: IAppState) => app.Reconcile.Cheque[type])
  const { resetSelectedRowRef } = yield select((app: IAppState) => app.Reconcile.Cheque)
  const updatedData = storeData.map((cheque: Cheque) => {
    if (cheque.chequeId === data.cheque_id) {
      return {
        ...cheque,
        chequeDate: data.cheque_date,
        chequeNo: data.cheque_number
      } as Cheque
    }

    return cheque
  })

  yield put(
    setCheques({
      data: updatedData,
      chequeType: type
    })
  )
  resetSelectedRowRef.receivedTable?.current?.resetRowSelection({})
  yield put(resetEditChequeDrawer())
  yield put(
    addAutoFadeNotification({
      type: NotificationType.SUCCESS,
      bodyText: 'Cheque updated Successfully'
    })
  )
}

export function* downloadChequeTransactionEffect() {
  const requestPayload: IFetchChequeRequestPayload = {
    page: 1,
    per_page: 999999,
    filters: {
      cheque_state: ChequeState.RECEIVED
    }
  }
  const response: IApiResponse = yield call(fetchChequeDetails, requestPayload)
  const receivedChequesData = formatChequesData(response.data)
  const formattedData = formatChequeReceivedDataForExcel(receivedChequesData.cheques)
  const ws = utils.json_to_sheet(formattedData, { origin: 'A5' })
  const wb = utils.book_new()
  const excelHeading = 'Collected Cheque Data'
  utils.sheet_add_aoa(
    ws,
    [
      [excelHeading],
      [],
      [`Summary Data Created On: ${dayjs.unix(dayjs().unix()).format('DD MMM, YYYY')}`],
      []
    ],
    {
      origin: 'A1'
    }
  )

  if (formattedData.length) {
    ws['!cols'] = getOrderColumnWidths(formattedData)
  }

  utils.book_append_sheet(wb, ws, 'Sheet 1')
  writeFileXLSX(wb, 'cheque_summary_data.xlsx')
}

export function* downloadDepositedChequeEffect(
  action: Action<{ listType: CHEQUE_TYPE; currentView: VIEW }>
) {
  const { currentView } = action.payload
  const Cheque: IChequeState = yield select((app: IAppState) => app.Reconcile.Cheque)
  const requestPayload: IFetchChequeRequestPayload = {
    page: 1,
    per_page: 999999,
    filters: {
      cheque_state: currentView,
      collected_on_interval: Cheque.filterTime?.startTime
        ? {
            start_time: Cheque.filterTime.startTime,
            end_time: Cheque.filterTime.endTime
          }
        : {
            start_time: dayjs().startOf('day').unix(),
            end_time: dayjs().endOf('day').unix()
          }
    }
  }
  const response: IApiResponse = yield call(fetchChequeDetails, requestPayload)
  const chequeDetails = formatChequesData(response.data)
  const formattedData = formatChequeReceivedDataForExcel(chequeDetails.cheques)
  const ws = utils.json_to_sheet(formattedData, { origin: 'A5' })
  const wb = utils.book_new()
  const excelHeading = `Deposited Cheque: ${
    currentView === ChequeState.DEPOSITED
      ? 'Deposited to Bank'
      : currentView === ChequeState.CLEARED
      ? 'Received Cash'
      : ''
  }`
  utils.sheet_add_aoa(
    ws,
    [
      [excelHeading],
      [],
      [`Summary Data Created On: ${dayjs.unix(dayjs().unix()).format('DD MMM, YYYY')}`],
      []
    ],
    {
      origin: 'A1'
    }
  )

  if (formattedData.length) {
    ws['!cols'] = getOrderColumnWidths(formattedData)
  }

  utils.book_append_sheet(wb, ws, 'Sheet 1')
  writeFileXLSX(wb, 'cheque_summary_data.xlsx')
}
